import { Check, Coffee, Navigation, Flower2, Sun, Leaf, Snowflake, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import { Reveal, SectionHead } from "@/components/Motion";
import VideoFeature from "@/components/VideoFeature";
import CTA from "@/components/CTA";
import { IMAGES } from "@/lib/site";

const PORTAL_URL = process.env.REACT_APP_PORTAL_URL;

const WHY = [
  { k: "3–4", v: "stops per average ride", d: "Cyclists don't ride past — they ride to. Routes become itineraries." },
  { k: "$38", v: "average spend per stop", d: "Hungry, happy, and unhurried. Riders linger longer than drive-by traffic." },
  { k: "72%", v: "plan rides around food & drink", d: "For most riders, the stop is the point. Your shop can be the reason for the ride." },
  { k: "2×", v: "weekend trail traffic growth", d: "Curated routes bring more riders past your door, season after season." },
];

const SEASONS = [
  { icon: Flower2, name: "Spring", promo: "Bloom & Brew rides" },
  { icon: Sun, name: "Summer", promo: "The Ice Cream Mile" },
  { icon: Leaf, name: "Fall", promo: "Harvest Trail Week" },
  { icon: Snowflake, name: "Winter", promo: "Warm Cup Circuit" },
];

export default function Businesses() {
  return (
    <main data-testid="page-businesses">
      <PageHero
        testid="businesses-hero"
        image={IMAGES.heroBusinesses}
        overline="For local businesses"
        lines={["Reach Cyclists While", "They're Out & Hungry."]}
        sub="VeloPaths puts your business on the routes cyclists actually ride — as an approved stop, not another ad in the noise."
      />

      <VideoFeature
        testid="section-video"
        overline="Watch"
        title="Why Velopaths was created."
        sub="Two minutes on how a Founding Advertiser listing puts your business on the routes riders actually ride."
        src="/videos/founding-advertiser-web.mp4"
        poster="/videos/founding-advertiser-poster.jpg"
        posterAlt="Cyclist riding a paved riverside path at golden hour"
        badge="Founding Advertiser · 2 min"
      />

      <section
        data-testid="section-advertiser-program"
        className="mx-auto max-w-7xl px-6 py-24 sm:px-10 sm:py-32 lg:px-16"
      >
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <h2
                data-testid="advertiser-program-heading"
                className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-snug text-[#0B2B22]"
              >
                The Advertiser Program
              </h2>
              <p className="mt-5 text-base sm:text-lg leading-relaxed text-[#52635B]">
                VeloPaths connects cyclists with local businesses along the routes they ride every
                day. When riders open the VeloPaths mobile app, they see curated stops — real
                businesses placed directly on their route at the exact moment they're nearby,
                hungry, thirsty, or ready to spend.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-5 text-base sm:text-lg leading-relaxed text-[#52635B]">
                As an approved stop, your business appears on the map inside the VeloPaths mobile
                app, complete with your details, photos, and offerings. This is location-based
                advertising designed specifically for cyclists — reaching riders near bike paths
                and destinations at the exact moment they're nearby.
              </p>
              <p className="mt-5 text-base sm:text-lg leading-relaxed text-[#52635B]">
                Founding Advertiser pricing is available for a limited time. Early adopters receive
                preferred pricing during the initial growth phase of the VeloPaths platform — a
                low-cost, low-risk way to get on the map first.
              </p>
            </Reveal>
            <Reveal delay={0.2} className="mt-9">
              <a
                href={PORTAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="btn-advertise-on-velopaths"
                className="inline-flex items-center gap-2 rounded-full bg-[#E05A47] px-7 py-3.5 text-sm font-bold text-white shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#c94a39]"
              >
                Reserve Your Location <ArrowUpRight size={15} />
              </a>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="rounded-[2rem] bg-[#0B2B22] p-8 text-[#F4F7F4] sm:p-10">
              <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#7FB998]">
                Founding Advertiser
              </p>
              <p className="mt-3">
                <span className="font-mono text-5xl font-bold">$99</span>
                <span className="text-[#F4F7F4]/60"> / year · per location</span>
              </p>
              <p className="mt-4 text-sm leading-relaxed text-[#F4F7F4]/70">
                Includes advertiser pin placement within an eligible zone and listing visibility in
                the VeloPaths Rider app — reaching cyclists near bike paths and destinations at the
                exact moment they're nearby.
              </p>
              <div className="mt-8 border-t border-[#F4F7F4]/15 pt-6">
                <p className="font-mono text-3xl font-bold text-[#E05A47]">
                  ≈ 2.5¢{" "}
                  <span className="font-mono text-base font-bold text-[#F4F7F4]/70">
                    per rider (example)
                  </span>
                </p>
                <p className="mt-2.5 text-sm leading-relaxed text-[#F4F7F4]/70">
                  If 4,000 riders use VeloPaths in your region this year, your advertising cost is
                  roughly two and a half cents per rider — for placement directly on the routes
                  they ride.
                </p>
              </div>
              <div className="mt-8 border-t border-[#F4F7F4]/15 pt-6">
                <p className="text-sm leading-relaxed text-[#F4F7F4]/70">
                  Founding Advertiser pricing is available for a limited time. Renewal pricing may
                  differ in future years as the platform expands and additional features are
                  introduced.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 sm:px-10 sm:pb-32 lg:px-16">
        <SectionHead
          overline="Why cyclists"
          title="The most valuable audience on two wheels."
          sub="Early partner-market metrics from our beta routes. Illustrative figures — full analytics come with every listing."
        />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {WHY.map((w, i) => (
            <Reveal key={w.v} delay={i * 0.1} className="h-full">
              <div className="h-full rounded-[1.75rem] border border-[#154234]/10 bg-white p-7">
                <p className="font-mono text-3xl font-bold text-[#E05A47]">{w.k}</p>
                <p className="mt-1 font-mono text-xs font-bold uppercase tracking-[0.15em] text-[#0B2B22]">
                  {w.v}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-[#52635B]">{w.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
          <div className="grid items-center gap-14 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <SectionHead
                overline="How listings appear"
                title="A storefront built for handlebars."
                sub="Your listing shows up as a curated pin with everything a rider needs at a glance — category, distance off the path, hours, and your rider deal. One tap and they're detouring to your door."
              />
            </div>
            <Reveal delay={0.15} className="lg:col-span-6">
              <div
                data-testid="listing-preview-card"
                className="rounded-[2rem] border border-[#154234]/10 bg-[#F4F7F4] p-6 shadow-2xl"
              >
                <img
                  src={IMAGES.cafe}
                  alt="Example listing photography"
                  loading="lazy"
                  decoding="async"
                  className="h-44 w-full rounded-2xl object-cover"
                />
                <div className="mt-5 flex items-center gap-3">
                  <span className="grid size-11 place-items-center rounded-xl bg-[#38A3A5] text-white">
                    <Coffee size={19} />
                  </span>
                  <div>
                    <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#52635B]">
                      Café · Mile 4.2 · 2 min detour
                    </p>
                    <p className="font-display text-xl font-bold tracking-tight text-[#0B2B22]">
                      Switchback Café
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="rounded-full bg-[#E05A47]/10 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-[#E05A47]">
                    Rider deal — free refills
                  </span>
                  <span className="rounded-full bg-[#2D6A4F]/10 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-[#2D6A4F]">
                    Open until 6 PM
                  </span>
                </div>
                <button
                  data-testid="btn-listing-detour"
                  onClick={() =>
                    toast("Detour preview", {
                      description: "In the app, riders tap once to re-route to this stop and back.",
                    })
                  }
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0B2B22] px-6 py-3 text-sm font-bold text-[#F4F7F4] transition-colors hover:bg-[#154234]"
                >
                  <Navigation size={14} /> Detour here
                </button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-10 sm:py-32 lg:px-16">
        <SectionHead
          overline="Promotions & seasonal highlights"
          title="The map changes with the seasons. So can you."
          sub="Founding Advertisers are eligible for promotion slots in our seasonal campaigns — platform-wide pushes that put your shop at the center of the ride."
        />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {SEASONS.map((s, i) => (
            <Reveal key={s.name} delay={i * 0.1} className="h-full">
              <div className="h-full rounded-[1.75rem] border border-[#154234]/10 bg-white p-7 transition-transform duration-500 hover:-translate-y-1.5">
                <span className="grid size-11 place-items-center rounded-xl bg-[#2D6A4F]/10 text-[#2D6A4F]">
                  <s.icon size={19} />
                </span>
                <p className="mt-5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#52635B]">
                  {s.name}
                </p>
                <p className="mt-1.5 font-display text-lg font-bold tracking-tight text-[#0B2B22]">
                  {s.promo}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTA
        overline="Advertise on VeloPaths"
        title="Put your shop on the route riders actually ride."
        sub="Founding Advertiser pricing — $99/year per location for a limited time. Create your listing in the Advertiser Portal."
        primary={{ label: "Become a Founding Advertiser", to: PORTAL_URL, testid: "btn-cta-advertise" }}
        secondary={{ label: "See rider experience", to: "/riders", testid: "btn-cta-see-riders" }}
      />
    </main>
  );
}
